/**
 * Saved views (DesignSystem 4.4 and 6.1): any filter, sort and grouping of a list, kept under
 * a name. A view is just a route and its query string, so the address bar is always the
 * source of truth: opening a view navigates to it, and a link to a view shows the same list
 * to anyone (`view=<name>` carries the name along).
 *
 * "Your views" live in this browser. "Team views" live on the hub (module 'views') and every
 * signed-in person sees them; they load from GET /api/ext/views when the hub offers it.
 */
import { create } from 'zustand';
import type { SharedView } from '@orgus/protocol';

export interface SavedView {
  id: string;
  name: string;
  /** Route path: '/sessions'. */
  path: string;
  /** Normalised query string without the leading '?'. */
  search: string;
  createdAt: number;
  shared?: boolean;
  createdBy?: string;
}

const KEY = 'fd.views';

/** Parameters that open a drawer or switch data source: never part of a view. */
const TRANSIENT_ALWAYS = ['view', 'mock', 'session', 'entry'];
const TRANSIENT_BY_PATH: Record<string, string[]> = {
  '/machines': ['machine'],
  '/projects': ['project'],
  '/pipelines': ['pipeline'],
};

/** The query string that identifies a view: transient keys dropped, keys sorted, empty values dropped. */
export function normalizeSearch(path: string, search: string | URLSearchParams): string {
  const p = new URLSearchParams(search);
  const drop = new Set([...TRANSIENT_ALWAYS, ...(TRANSIENT_BY_PATH[path] ?? [])]);
  const entries = [...p.entries()].filter(([k, v]) => !drop.has(k) && v !== '');
  entries.sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
  return new URLSearchParams(entries).toString();
}

function read(): SavedView[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((v): v is SavedView => !!v && typeof v.id === 'string' && typeof v.path === 'string' && typeof v.name === 'string') : [];
  } catch {
    return [];
  }
}

function write(list: SavedView[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // private mode: views last for this tab
  }
}

interface ViewsStore {
  mine: SavedView[];
  team: SavedView[];
  /** Whether the hub offers team views (GET /api/ext/views answered). */
  teamAvailable: boolean;
}

export const useViews = create<ViewsStore>()(() => ({ mine: typeof localStorage === 'undefined' ? [] : read(), team: [], teamAvailable: false }));

function newId(): string {
  return `v-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Save (or replace, by name and path) a view in this browser. */
export function saveMyView(v: { name: string; path: string; search: string }): SavedView {
  const search = normalizeSearch(v.path, v.search);
  const view: SavedView = { id: newId(), name: v.name.trim(), path: v.path, search, createdAt: Date.now() };
  const rest = useViews.getState().mine.filter((x) => !(x.path === v.path && x.name.toLowerCase() === view.name.toLowerCase()));
  const mine = [...rest, view];
  write(mine);
  useViews.setState({ mine });
  return view;
}

export function deleteMyView(id: string): SavedView | undefined {
  const st = useViews.getState();
  const gone = st.mine.find((v) => v.id === id);
  const mine = st.mine.filter((v) => v.id !== id);
  write(mine);
  useViews.setState({ mine });
  return gone;
}

/** Put a deleted view back (the Undo of delete). */
export function restoreMyView(v: SavedView): void {
  const mine = [...useViews.getState().mine.filter((x) => x.id !== v.id), v];
  write(mine);
  useViews.setState({ mine });
}

export function setTeamViews(list: SharedView[] | null): void {
  if (!list) {
    useViews.setState({ team: [], teamAvailable: false });
    return;
  }
  useViews.setState({
    teamAvailable: true,
    team: list.map((v) => ({ id: v.id, name: v.name, path: v.path, search: v.search, createdAt: v.createdAt, createdBy: v.createdBy, shared: true })),
  });
}

/** Every view for a path: yours first, then the team's. */
export function viewsFor(path: string, st: Pick<ViewsStore, 'mine' | 'team'>): SavedView[] {
  return [...st.mine.filter((v) => v.path === path), ...st.team.filter((v) => v.path === path)];
}

/** The saved view the address shows right now, if any. */
export function matchView(path: string, search: string, views: SavedView[]): SavedView | undefined {
  const n = normalizeSearch(path, search);
  if (!n) return undefined;
  return views.find((v) => v.path === path && v.search === n);
}

/** Route plus query for a view, with its name so a shared link can say what it is. */
export function viewHref(v: Pick<SavedView, 'path' | 'search' | 'name'>): string {
  const p = new URLSearchParams(v.search);
  p.set('view', v.name);
  return `${v.path}?${p.toString()}`;
}
